import { Flex, Pagination, Table } from 'antd';
import type { TableProps } from 'antd';
import { useMemo, type ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import LoaderEcho from '@/components/LoaderEcho';
import { useTableSort } from '@/hooks/tables/useTableSort';
import { useTablePagination } from '@/hooks/tables/useTablePagination';
import type { DataTableEchoProps, PaginationProps, TableData } from '@/types';
import { sortOrderForColumn, tableOrderToSortField } from './dataTableEchoSortUtils';

const DEFAULT_PAGINATION = {
  summary: true,
  pageSizeOptions: [5, 10, 15, 20],
  defaultPageSize: 5,
};

const buildPaginationTotal = (
  summary: PaginationProps['summary']
): ((total: number, range: [number, number]) => ReactNode) | undefined => {
  if (summary === false) {
    return undefined;
  }
  return (total, range) => {
    const [fromPage, toPage] = range;
    return typeof summary === 'function'
      ? summary(fromPage, toPage, total)
      : `${fromPage}-${toPage} of ${total}`;
  };
};

/**
 * Echo data table: Ant Design `Table` with `columns` + `dataSource` (`TableData`), client-side sort + bottom pagination.
 */
const DataTableEcho = ({
  columns,
  dataSource,
  rowKey = 'id',
  tableProps,
  isLoading = false,
  initialState,
  dataTestId,
  pagination: customPagination = false,
  tableWrapperClassName = '',
  onSort,
  onPageChange,
  serverSideTotal,
}: DataTableEchoProps) => {
  const shouldPaginate = !!customPagination;
  const isServerSide = serverSideTotal !== undefined;
  const numberOfItems = isServerSide ? serverSideTotal : dataSource.length;
  const pagination = { ...DEFAULT_PAGINATION, ...(customPagination || {}) };

  const { sortedData, sortField, applySortFromTable } = useTableSort(
    dataSource,
    onSort,
    initialState?.sorting
  );

  const { page, pageSize, pageStart, pageEnd, setPage, setPageSize } = useTablePagination(
    numberOfItems,
    pagination
  );

  const { summary, pageSizeOptions } = pagination;
  const pageData = isServerSide
    ? dataSource
    : sortedData.slice(pageStart, shouldPaginate ? pageEnd : numberOfItems);

  const handleTableChange: TableProps<TableData>['onChange'] = (_pagination, _filters, sorter) => {
    const normalized = Array.isArray(sorter) ? sorter[0] : sorter;
    applySortFromTable(
      tableOrderToSortField(normalized?.columnKey ?? normalized?.field, normalized?.order)
    );
  };

  const columnsWithSortOrder = useMemo(
    () =>
      columns.map((column) =>
        column.sorter
          ? {
              ...column,
              sortOrder: sortOrderForColumn(
                column.key ?? ('dataIndex' in column ? column.dataIndex : undefined),
                sortField
              ),
            }
          : column
      ),
    [columns, sortField]
  );

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    onPageChange?.(nextPage);
  };

  const paginationTotal = buildPaginationTotal(summary);

  if (isLoading) {
    return (
      <Flex justify="center" align="center" style={{ minHeight: '25dvh' }}>
        <LoaderEcho />
      </Flex>
    );
  }

  return (
    <div data-testid={dataTestId}>
      <div className={twMerge(tableWrapperClassName)}>
        <Table<TableData>
          {...(tableProps ?? {})}
          rowKey={rowKey}
          columns={columnsWithSortOrder}
          dataSource={pageData}
          pagination={false}
          onChange={handleTableChange}
          locale={{ emptyText: 'No data', ...tableProps?.locale }}
          showSorterTooltip={false}
        />
      </div>
      {numberOfItems > 0 && shouldPaginate && (
        <Flex justify="flex-start" className="pt-4" data-test-id="DataTable__pagination">
          <Pagination
            total={numberOfItems}
            current={page}
            pageSize={pageSize}
            onChange={handlePageChange}
            onShowSizeChange={(_current, size) => setPageSize(size)}
            pageSizeOptions={pageSizeOptions}
            showSizeChanger
            showTotal={paginationTotal}
          />
        </Flex>
      )}
    </div>
  );
};

export default DataTableEcho;
